import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useCommercialInteriorDesign } from "../useHook";
import { ImageWithFallback } from "../../../Utils/loader";
import Swal from "sweetalert2";
import Loader from "../../../Utils/loader";

const CommercialInteriorDesignDetails = () => {
  const { id } = useParams();
  const { getCommercialInteriorDesignById } = useCommercialInteriorDesign();
  const [interiorData, setInteriorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("description");

  useEffect(() => {
    loadCommercialInteriorDesignDetails();
  }, [id]);

  const loadCommercialInteriorDesignDetails = async () => {
    try {
      setLoading(true);
      await getCommercialInteriorDesignById(id, setInteriorData);
    } catch (error) {
      console.error("Error loading commercial interior design details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load commercial interior design details.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader message="Loading commercial interior design details..." />;
  }

  if (!interiorData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">
            Commercial Interior Design Not Found
          </h2>
          <p className="text-gray-500">
            The commercial interior design you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-100 p-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold mb-1">Commercial Interior Design Details</h2>
        </div>
      </div>

      <div className="max-w-7xl mx-auto my-6 p-6 bg-white shadow-lg rounded-lg">
        <div className="mb-6">
          <h2 className="text-3xl font-bold">{interiorData.title}</h2>
          <p className="text-sm text-gray-500 mt-2">
            Created: {new Date(interiorData.created_at).toLocaleDateString()}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="mb-2">
              <span className="font-semibold">Room Size: </span>
              {interiorData.room_size}
            </p>
            <p className="mb-2">
              <span className="font-semibold">Design Type: </span>
              {interiorData.design_type}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex border-b border-gray-300 mb-4">
            <button
              className={`py-2 px-4 font-medium focus:outline-none ${
                activeTab === "description"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={`py-2 px-4 font-medium focus:outline-none ${
                activeTab === "images"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("images")}
            >
              Images
            </button>
          </div>

          {activeTab === "description" && (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: interiorData.description }}
            />
          )}

          {activeTab === "images" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {interiorData.images?.map((img, index) => (
                <div key={img.id || index} className="relative">
                  <ImageWithFallback
                    src={img.image_url}
                    alt={`Commercial Interior Design ${index + 1}`}
                    className="w-full h-48 object-cover rounded shadow-md"
                  />
                  {img.is_primary && (
                    <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                      Primary
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommercialInteriorDesignDetails;
